import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { SimpleLayout } from './components/SimpleLayout';
import { BasicLayout } from './components/BasicLayout';
import ErrorBoundary from './components/ErrorBoundary';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Upload } from './pages/Upload';
import { Documents } from './pages/Documents';
import { TestDashboard } from './pages/TestDashboard';
import { ExpenseForm } from './pages/ExpenseForm';
import { PropertyExpenses } from './pages/PropertyExpenses';
import { DocumentReview } from './pages/DocumentReview';
import { DebugPage } from './pages/DebugPage';
import { TagsAndServiceTypes } from './pages/TagsAndServiceTypes';
import DocumentsManagement from './pages/DocumentsManagement';
import Properties from './pages/Properties';
import PropertyDetails from './pages/PropertyDetails';
import PropertyDocuments from './pages/PropertyDocuments';
import { PropertyEdit } from './pages/PropertyEdit';
import RulesManagement from './pages/RulesManagement';
import { Settings } from './pages/Settings';
import { SettingsSimple } from './pages/SettingsSimple';
import { EmailWhitelist } from './pages/EmailWhitelist';
import { EmailStatus } from './pages/EmailStatus';
import { BusinessExpenses } from './pages/BusinessExpenses';
import { DuplicateCheck } from './pages/DuplicateCheck';
import { Messages } from './pages/Messages';
import { MessagesEnhanced } from './pages/MessagesEnhanced';
import { MessagesConversation } from './pages/MessagesConversation';
import { AdminUsers } from './pages/AdminUsers';
import { AdminRoute } from './components/AdminRoute';
import { Contacts } from './pages/Contacts';
import { SystemStatus } from './pages/SystemStatus';
import PropertyGallery from './pages/PropertyGallery';
import RentalExperiment from './pages/RentalExperiment';
import RentalPortfolio from './pages/RentalPortfolio';
import RentalPortfolioEnhanced from './pages/RentalPortfolioEnhanced';
import { PlaywrightDebug } from './pages/PlaywrightDebug';
import PropertyManagementTest from './pages/PropertyManagementTest';
import Vendors from './pages/Vendors';
import { Diagnostics } from './pages/Diagnostics';
import { AITest } from './pages/AITest';
import ManualProcess from './pages/ManualProcess';
import { UnassignedImages } from './pages/UnassignedImages';
import { HOAEmails } from './pages/HOAEmails';
import HOADashboard from './pages/HOADashboard';
import { UnifiedInbox } from './pages/UnifiedInbox';
import { RetailerEmails } from './pages/RetailerEmails';
import EmailDashboard from './pages/EmailDashboard';
import SharedDocuments from './pages/SharedDocuments';
import { Subscriptions } from './pages/Subscriptions';
import { PocketSmith } from './pages/PocketSmith';
import { PocketSmithRules } from './pages/PocketSmithRules';
import { PocketSmithAccounts } from './pages/PocketSmithAccounts';
import { PocketSmithAnalytics } from './pages/PocketSmithAnalytics';
import { PropertySpending } from './pages/PropertySpending';
import { PropertyLedger } from './pages/PropertyLedger';
import { TechStack } from './pages/TechStack';
import { LeaseQA } from './pages/LeaseQA';
import { CurrentLeases } from './pages/CurrentLeases';
import { LeaseTemplates } from './pages/LeaseTemplates';
import { HOAPortal } from './pages/HOAPortal';
import { HOAStatus } from './pages/HOAStatus';
import { EmailIntelligence } from './pages/EmailIntelligence';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/shared/:token" element={<SharedDocuments />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <BasicLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="properties" element={<Properties />} />
        <Route path="properties/:id" element={<PropertyDetails />} />
        <Route path="properties/:id/edit" element={<PropertyEdit />} />
        <Route path="properties/:id/documents" element={<PropertyDocuments />} />
        <Route path="properties/:id/gallery" element={<PropertyGallery />} />
        <Route path="properties/:propertyId/expenses" element={<PropertyExpenses />} />
        {/* Main Pages */}
        <Route path="documents" element={<Documents />} />
        <Route path="expenses" element={<BusinessExpenses />} />
        <Route path="expenses/new" element={<ExpenseForm />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="tech-stack" element={<TechStack />} />
        <Route path="pocketsmith" element={<PocketSmith />} />
        <Route path="pocketsmith/rules" element={<PocketSmithRules />} />
        <Route path="pocketsmith/accounts" element={<PocketSmithAccounts />} />
        <Route path="pocketsmith/analytics" element={<PocketSmithAnalytics />} />
        <Route path="pocketsmith/property-spending" element={<PropertySpending />} />
        <Route path="ledger" element={<ErrorBoundary><PropertyLedger /></ErrorBoundary>} />
        <Route path="hoa" element={<ErrorBoundary><HOAEmails /></ErrorBoundary>} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="lease-qa" element={<LeaseQA />} />
        <Route path="current-leases" element={<CurrentLeases />} />
        <Route path="lease-templates" element={<LeaseTemplates />} />
        <Route path="hoa-portal" element={<HOAPortal />} />
        <Route path="hoa-status" element={<HOAStatus />} />

        {/* Settings */}
        <Route path="tags" element={<TagsAndServiceTypes />} />
        <Route path="email-status" element={<ErrorBoundary><EmailStatus /></ErrorBoundary>} />
        <Route path="email-intelligence" element={<ErrorBoundary><EmailIntelligence /></ErrorBoundary>} />
        <Route path="admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
        <Route path="admin/system-status" element={<AdminRoute><SystemStatus /></AdminRoute>} />

        {/* Legacy Routes - Redirect to new locations */}
        <Route path="documents/review" element={<Navigate to="/documents" replace />} />
        <Route path="documents/management" element={<Navigate to="/documents" replace />} />
        <Route path="business-expenses" element={<Navigate to="/expenses" replace />} />
        <Route path="hoa-emails" element={<Navigate to="/hoa" replace />} />
        <Route path="unified-inbox" element={<Navigate to="/documents" replace />} />
        <Route path="retailer-emails" element={<Navigate to="/documents" replace />} />
        <Route path="email-dashboard" element={<Navigate to="/documents" replace />} />
        <Route path="messages" element={<Navigate to="/coming-soon" replace />} />
        <Route path="unassigned-images" element={<Navigate to="/coming-soon" replace />} />

        {/* Coming Soon Page */}
        <Route path="coming-soon" element={
          <div className="p-8 text-white">
            <h1 className="text-3xl font-bold mb-4">🚀 Coming Soon</h1>
            <div className="bg-gray-800 rounded-lg p-6 space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h2 className="text-xl font-semibold mb-2">📱 OpenPhone Integration</h2>
                <p className="text-gray-400">SMS and call management directly in your dashboard</p>
              </div>
              <div className="border-l-4 border-green-500 pl-4">
                <h2 className="text-xl font-semibold mb-2">📲 Mobile App</h2>
                <p className="text-gray-400">Access your properties and documents on the go</p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h2 className="text-xl font-semibold mb-2">📊 Advanced Analytics</h2>
                <p className="text-gray-400">Deep insights into your property portfolio performance</p>
              </div>
            </div>
          </div>
        } />

        {/* Debug Routes - Keep for development */}
        <Route path="debug" element={<DebugPage />} />
        <Route path="diagnostics" element={<Diagnostics />} />
        <Route path="ai-test" element={<AITest />} />
        <Route path="manual-process" element={<ManualProcess />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <ErrorBoundary>
        <AuthProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                style: {
                  background: '#10b981',
                },
              },
              error: {
                style: {
                  background: '#ef4444',
                },
              },
            }}
          />
          <AppRoutes />
        </AuthProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App;